import { Injectable } from '@angular/core';
import { EMPTY, first, forkJoin, from, map, switchMap, tap } from 'rxjs';
import { TemplatesDomainService } from 'src/app/modules/templates/services/templates-domain.service';
import { OrdersDomainService } from 'src/app/modules/orders/services/orders-domain.service';
import { FieldsDomainService } from 'src/app/modules/fields/services/fields-domain.service';
import { OfflinePendingToSyncType } from 'src/app/shared/utils/offline/constants/offline.constants';
import { UsersDomainService } from 'src/app/modules/users/services/users-domain.service';
import { OrdersRepositoryService } from 'src/app/modules/orders/services/orders-repositoy.service';
import { UsersRepositoryService } from 'src/app/modules/users/services/users-repository.service';
import { FieldsRepositoryService } from 'src/app/modules/fields/services/fields-repository.service';
import { TemplatesRepositoryService } from 'src/app/modules/templates/services/templates-repository.service';
import { MachinesDomainService } from 'src/app/modules/machines/services/machines-domain.service';
import { MachinesRepositoryService } from 'src/app/modules/machines/services/machines-repository.service';

@Injectable({
  providedIn: 'root',
})
export class SyncDataService {
  constructor(
    private readonly templatesDomainService: TemplatesDomainService,
    private readonly ordersDomainService: OrdersDomainService,
    private readonly fieldsDomainService: FieldsDomainService,
    private readonly usersDomainService: UsersDomainService,
    private readonly ordersRepositoryService: OrdersRepositoryService,
    private readonly usersRepositoryService: UsersRepositoryService,
    private readonly fieldsRepositoryService: FieldsRepositoryService,
    private readonly templatesRepositoryService: TemplatesRepositoryService,
    private readonly machinesDomainService: MachinesDomainService,
    private readonly machinesRepositoryService: MachinesRepositoryService
  ) {}

  storeLocalData = () => {
    return forkJoin({
      usersInOrders: this.usersDomainService.getAllInOrders(),
      fields: this.fieldsDomainService.getAll(),
      templatesResume: this.templatesDomainService.getAllResume(),
      templates: this.templatesDomainService.getAll(),
      ordersResume: this.ordersDomainService.getAllResume(),
      orders: this.ordersDomainService.getAll(),
      machines: this.machinesDomainService.getAllDetail(),
    }).pipe(
      switchMap(
        async ({
          usersInOrders,
          fields,
          templatesResume,
          templates,
          ordersResume,
          orders,
          machines,
        }) => {
          await this.usersRepositoryService.cleanAndSeed(usersInOrders);
          await this.fieldsRepositoryService.cleanAndSeed(fields);
          await this.templatesRepositoryService.cleanAndSeedTemplatesResumes(templatesResume);
          await this.templatesRepositoryService.cleanAndSeedTemplates(templates);
          await this.ordersRepositoryService.cleanAndSeedOrdersResume(ordersResume);
          await this.ordersRepositoryService.cleanAndSeedOrders(orders);
          await this.machinesRepositoryService.cleanAndSeedMachines(machines);
        }
      ),
      map(() => EMPTY)
    );
  };

  syncWithServer = () => {
    return forkJoin([
      this.ordersDomainService.syncPendingToCreate(),
      this.ordersDomainService.syncPendingToExecute(),
      this.ordersDomainService.syncPendingToDraft(),
    ]).pipe(
      tap(() => console.log(`Synced everything`)),
      map(() => EMPTY)
    );
  };

  calculatePendingToSync = () => {
    return forkJoin({
      ordersCreated: from(
        this.ordersRepositoryService.getOrdersToPersistCountFilterByType(
          OfflinePendingToSyncType.OrderCreated
        )
      ),
      ordersExecuted: from(
        this.ordersRepositoryService.getOrdersToPersistCountFilterByType(
          OfflinePendingToSyncType.OrderExecuted
        )
      ),
      ordersDrafted: from(
        this.ordersRepositoryService.getOrdersToPersistCountFilterByType(
          OfflinePendingToSyncType.OrderDrafted
        )
      ),
    }).pipe(first());
  };
}
